import React, { useState } from 'react';
import { API_BASE } from '../../services/api';
import { WizardData } from '../ProbateWizard';
import { ProbateResponse, EstateAssets, ConsistencyFinding } from '../../types';
import { EstateBuilder } from '../EstateBuilder';
import { IHTCalculatorPanel } from '../IHTCalculatorPanel';
import { ReportBody } from '../results/ReportBody';
import { InputsUsedPanel } from '../results/InputsUsedPanel';
import { PropertyRationalePanel } from '../results/PropertyRationalePanel';
import { ProbateReport } from '../results/ProbateReport';
import { Loader2, Play, WifiOff, FileJson, Download, ChevronDown, ChevronUp, Database, Calculator, Gift, FileText, Gauge, ListChecks, ShieldAlert } from 'lucide-react';
import { WizardResearchPanel } from '../WizardResearchPanel';
import { DebugPanel } from '../DebugPanel';
import { postAudit } from '../probateApi';
import { setLastAuditMeta as setGlobalDebug, newAuditId } from '../probateDebugStore';
import { ErrorBoundary } from '../ErrorBoundary';
import { generateAndOpenProbateReport } from '../utils/generateProbateReport';
import { DeadlineTracker } from '../results/DeadlineTracker';
import { HMRCFormMapper } from '../results/HMRCFormMapper';
import { ExecutorRiskAlerts } from '../results/ExecutorRiskAlerts';
import { EstimateConfidence } from '../results/EstimateConfidence';
import { EstateCompleteness } from '../results/EstateCompleteness';
import { SubmissionChecklist } from '../results/SubmissionChecklist';
import { TaxSourceBreakdown } from '../results/TaxSourceBreakdown';
import { ExecutorTaxWarning } from '../results/ExecutorTaxWarning';
import { RnrbExplanation } from '../results/RnrbExplanation';
import { GiftTaperBreakdown } from '../results/GiftTaperBreakdown';
import { ProbateRiskOverview } from '../results/ProbateRiskOverview';
import { ComplexityScorePanel } from '../results/ComplexityScorePanel';
import { IHTRiskFlagsPanel } from '../results/IHTRiskFlagsPanel';
import { ValidationGuardBanner } from '../results/ValidationGuardBanner';
import { TrustEstateInclusionNotice } from '../results/TrustEstateInclusionNotice';
import { ScenarioInlineHint, getInlineHint } from '../results/ScenarioInlineHint';
import { classifyEstate, detectComplexityFlags } from '../../server/services/scenarioClassifier';
import { ReportOutdatedBanner } from '../results/ReportOutdatedBanner';
import { CollapsibleSection } from '../results/CollapsibleSection';
import { EvidenceRequestPanel } from '../results/EvidenceRequestPanel';
import { UpgradeBanner } from '../results/UpgradeBanner';
import { LockedNextStepsPreview } from '../results/LockedNextStepsPreview';
import { FinalUpgradeCTA } from '../results/FinalUpgradeCTA';
import { PricingPanel } from '../results/PricingPanel';
import { PaymentPendingCard } from '../results/PaymentPendingCard';
import { PRICE_LABEL, PAYMENT_FAILED_MESSAGE } from '../lib/commercialCopy';
import { BasicEstateSummary } from '../results/BasicEstateSummary';
import { useAuth } from '../auth/AuthContext';
import { AuthModal } from '../auth/AuthModal';

interface Props {
    data: WizardData;
    updateAssets: (assets: EstateAssets) => void;
    goToStep?: (stepId: string) => boolean;
    estateId?: string | null;
    /**
     * When true, run the audit automatically as soon as this step renders.
     * Set by the wizard when the user arrived here via "open report" or
     * the post-Stripe unlock flow — they should see the report, not a
     * "Run Probate Audit" button.
     */
    autoRun?: boolean;
}

export const ReviewStep: React.FC<Props> = ({ data, updateAssets, goToStep, estateId, autoRun = false }) => {
    const { user } = useAuth();
    const sessionNonce = React.useRef(Math.random().toString(36).slice(2)).current;
    const [showDebug, setShowDebug] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [lastAuditResponse, setLastAuditResponse] = useState<ProbateResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [auditError, setAuditError] = useState<string | null>(null);
    const [downloadPreparing, setDownloadPreparing] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showUpgradeReason, setShowUpgradeReason] = useState<string | null>(null);
    // Set when user returns from Stripe but webhook hasn't granted access yet
    const [paymentPending, setPaymentPending] = useState(false);
    const paymentPendingRef = React.useRef(false);
    // Mirrors `loading` in a ref so timer callbacks see the latest value
    // (closures would otherwise capture a stale `loading`).
    const loadingRef = React.useRef(false);
    React.useEffect(() => { loadingRef.current = loading; }, [loading]);

    const handleDownloadReport = () => {
        setDownloadPreparing(true);
        // Brief delay so the user sees the "Preparing…" state
        setTimeout(() => {
            generateAndOpenProbateReport(data, lastAuditResponse);
            setDownloadPreparing(false);
        }, 600);
    };

    const handleRunAudit = async () => {
        // Anonymous users must create an account first
        if (!user) {
            setShowAuthModal(true);
            return;
        }

        const id = newAuditId();
        setGlobalDebug({
            id,
            url: "/api/audit",
            method: "POST",
            startedAt: Date.now(),
            stage: "clicked"
        });

        setLoading(true);
        setAuditError(null);
        setLastAuditResponse(null);
        setShowUpgradeReason(null);

        const { data: responseData, meta } = await postAudit(data, "", estateId);

        if (!meta.ok || !responseData) {
            // Try to extract structured error JSON from the response
            // meta.error format: "HTTP 401: Unauthorized - {json}" or plain text
            let errBody: any = null;
            try {
                const dashIdx = (meta.error || '').indexOf(' - ');
                if (dashIdx > 0) {
                    errBody = JSON.parse(meta.error!.slice(dashIdx + 3));
                }
            } catch { /* not JSON — fall through */ }

            if (errBody?.error === 'limit_reached') {
                setShowUpgradeReason(errBody.message);
                setLoading(false);
                return;
            }
            if (errBody?.error === 'account_required') {
                setShowAuthModal(true);
                setLoading(false);
                return;
            }

            setAuditError(meta.error || "The audit request failed");
            setLoading(false);
            return;
        }

        setLastAuditResponse(responseData);
        setLoading(false);
    };

    // Derived Logic for Reason Rendering
    const whyItems = lastAuditResponse?.actionPack?.why || [];
    // Pending items from backend blockers
    const pendingItems = lastAuditResponse?.derived?.iht_calc?.blockers || [];

    const ihtCalc = lastAuditResponse?.derived?.iht_calc;
    const fullAccess = lastAuditResponse?.fullAccess === true;

    // Cross-field consistency conflicts (paid `derived` only; free tier strips
    // them). Undefined when absent ⇒ ReportBody shows no consistency UI.
    const consistencyFindings: ConsistencyFinding[] | undefined = lastAuditResponse?.derived?.consistency_findings;

    // Contextual upgrade message from backend (with safe fallbacks)
    const upgradeMessage: string =
        lastAuditResponse?.upgrade?.message ||
        lastAuditResponse?.upgrade?.genericMessage ||
        'Unlock the full inheritance tax calculation and report.';
    const complexityHints = lastAuditResponse?.upgrade?.complexityHints ?? null;

    // Scenario classification for UX banners
    const scenario = lastAuditResponse
        ? classifyEstate(ihtCalc, data.EstateAssets, data.EstateProfile)
        : null;
    const complexityFlags = lastAuditResponse
        ? detectComplexityFlags(ihtCalc, data.EstateAssets, data.EstateProfile)
        : null;

    // ── Auto-run audit when user becomes available ──────────
    // Covers two cases:
    //   1. Post-payment: Stripe redirects back with ?payment=success
    //   2. Post-registration: user just signed up and we should run immediately
    //
    // IMPORTANT: The ?payment=success query param is a UX hint only — it does
    // NOT grant access. Access is controlled by the backend's canAccessFullReport
    // check, which reads from the database. The Stripe webhook must have
    // processed and unlocked the estate before fullAccess becomes true.
    const prevUserRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (!user) { prevUserRef.current = null; return; }

        // Post-payment redirect — mark as pending until webhook confirms
        const params = new URLSearchParams(window.location.search);
        if (params.get('payment') === 'success' && !lastAuditResponse) {
            window.history.replaceState({}, '', window.location.pathname);
            paymentPendingRef.current = true;
            setPaymentPending(true);
            handleRunAudit();
            return;
        }

        // User just logged in/registered (was null, now has value) and no results yet
        if (prevUserRef.current === null && !lastAuditResponse && !loading) {
            prevUserRef.current = user.id;
            // Small delay to let auth state settle
            setTimeout(() => handleRunAudit(), 300);
            return;
        }

        prevUserRef.current = user.id;
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    // Explicit auto-run when the wizard opens this step post-unlock or via a
    // "Open report" click. The effect above only fires when `user` transitions
    // from null→non-null, which doesn't happen when the wizard remounts with
    // an already-authenticated user (e.g. clicking a report tile on the home
    // page). Without this, the user would see the "Run Probate Audit" button
    // even though they just asked to open the report.
    const autoRunFiredRef = React.useRef(false);
    React.useEffect(() => {
        if (!autoRun || autoRunFiredRef.current) return;
        if (!user) return;
        if (lastAuditResponse || loading) return;
        autoRunFiredRef.current = true;
        handleRunAudit();
    }, [autoRun, user, lastAuditResponse, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear paymentPending once access is confirmed by the backend
    React.useEffect(() => {
        if (paymentPending && lastAuditResponse?.fullAccess) {
            setPaymentPending(false);
            paymentPendingRef.current = false;
        }
    }, [paymentPending, lastAuditResponse]);

    // If payment was made but webhook hasn't arrived yet (fullAccess still false),
    // run a small spaced retry schedule before the manual "Refresh status"
    // button (30s) becomes the user's recovery path. All retries reuse the
    // existing handleRunAudit function — single source of truth.
    //
    // Schedule: 4s, 10s, 25s. Stops automatically when paymentPending clears,
    // when fullAccess flips true, or on unmount.
    const PAYMENT_PENDING_RETRY_DELAYS_MS = [4_000, 10_000, 25_000];
    React.useEffect(() => {
        if (!paymentPendingRef.current || !lastAuditResponse || lastAuditResponse.fullAccess) return;

        const timers: ReturnType<typeof setTimeout>[] = [];
        PAYMENT_PENDING_RETRY_DELAYS_MS.forEach((delay, idx) => {
            timers.push(
                setTimeout(() => {
                    // Bail if pending cleared, access granted, or another
                    // refresh is already in flight (overlap guard).
                    if (!paymentPendingRef.current) return;
                    if (loadingRef.current) return;
                    if (process.env.NODE_ENV !== 'production') {
                        console.log(`payment pending retry ${idx + 1}/${PAYMENT_PENDING_RETRY_DELAYS_MS.length}`);
                    }
                    handleRunAudit();
                }, delay),
            );
        });
        return () => { timers.forEach(clearTimeout); };
    }, [lastAuditResponse]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpgrade = async () => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }

        // Call billing checkout — redirect to Stripe
        try {
            const currentUrl = window.location.origin + window.location.pathname;
            // Include estateId in the success URL so the post-payment screen
            // knows which estate to poll for webhook-confirmed unlock. Without
            // this hint the app would have to fall back to localStorage, which
            // may point at a different estate on the same browser.
            const successSuffix = estateId
                ? `?payment=success&estateId=${encodeURIComponent(estateId)}`
                : '?payment=success';
            const res = await fetch(`${API_BASE}/api/billing/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    priceId: 'full_report_unlock', // Product key — server resolves real Stripe price
                    successUrl: currentUrl + successSuffix,
                    cancelUrl: currentUrl + '?payment=cancelled',
                    metadata: estateId ? { estateId } : undefined,
                }),
            });
            const result = await res.json();

            if (result.url) {
                // Redirect to Stripe checkout
                window.location.href = result.url;
            } else if (result.error === 'Billing is not enabled') {
                // Billing not configured — dev/staging fallback
                alert('Payment is not yet configured. The full report will be available once Stripe is set up.');
            } else {
                setAuditError(PAYMENT_FAILED_MESSAGE);
            }
        } catch {
            setAuditError(PAYMENT_FAILED_MESSAGE);
        }
    };

    return (
        <div className="space-y-8 relative animate-in fade-in duration-500">
            {/* Main Action Button */}
            {!lastAuditResponse && !showUpgradeReason && (
                <div className="flex flex-col items-center py-10 gap-4">
                    {/* Account prompt for anonymous users */}
                    {!user && !loading && (
                        <div className="max-w-md text-center mb-2">
                            <p className="text-[13px] text-[#3F3D39] leading-relaxed">
                                To run your assessment, you'll need a free account.
                                This keeps your data safe and lets you return to your results.
                            </p>
                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAuthModal(true)}
                                    className="px-5 py-3 text-[13px] font-medium tracking-[0.02em] text-white bg-[#3730A3] hover:bg-[#2E2780] transition-colors"
                                >
                                    Create free account
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowAuthModal(true); }}
                                    className="px-5 py-3 text-[13px] font-medium tracking-[0.02em] text-[#3F3D39] bg-transparent border border-[#E8E4DB] hover:bg-[#F3EFE6] transition-colors"
                                >
                                    Sign in
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Run button — only shown when logged in */}
                    {user && (
                        <button
                            type="button"
                            onClick={handleRunAudit}
                            disabled={loading}
                            className={`
                                flex items-center gap-2 px-8 py-4 text-[15px] font-medium tracking-[0.02em] text-white transition-colors
                                ${loading ? 'bg-[#AFA99F] cursor-not-allowed' : 'bg-[#3730A3] hover:bg-[#2E2780]'}
                            `}
                        >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
                            {loading ? 'Analysing estate…' : 'Run probate audit'}
                        </button>
                    )}
                </div>
            )}

            {/* Error State */}
            {auditError && (
                <div className="max-w-xl mx-auto bg-white border border-[#1A1A1A] p-5">
                    <div className="flex items-start gap-3">
                        <WifiOff className="w-5 h-5 text-[#1A1A1A] flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h4 className="font-serif text-[16px] font-medium text-[#1A1A1A] m-0">We couldn't run the check</h4>
                            <p className="text-[13px] text-[#3F3D39] mt-1 italic font-serif leading-relaxed">
                                The app couldn't reach the auditor service. Please try again.
                            </p>
                            <p className="text-[11px] text-[#6B6762] mt-2 font-mono break-all">{auditError}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Audit Limit Reached */}
            {showUpgradeReason && !lastAuditResponse && (
                <div className="max-w-xl mx-auto bg-[#FAF3E0] border border-[#E8D89A] p-5">
                    <div className="flex items-start gap-3">
                        <Play className="w-4 h-4 text-[#7A5A00] flex-shrink-0 mt-1" />
                        <div className="flex-1">
                            <h4 className="font-serif text-[16px] font-medium text-[#1A1A1A] m-0">Free limit reached</h4>
                            <p className="text-[13px] text-[#3F3D39] mt-1 leading-relaxed">{showUpgradeReason}</p>
                            <button
                                onClick={handleUpgrade}
                                className="mt-3 px-5 py-3 text-[13px] font-medium tracking-[0.02em] text-white bg-[#3730A3] hover:bg-[#2E2780] transition-colors"
                            >
                                Unlock full report
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Auth Modal */}
            {showAuthModal && (
                <AuthModal
                    onClose={() => {
                        setShowAuthModal(false);
                        // Auto-run audit after successful auth if we haven't run yet
                        // (user just registered/logged in to unlock the audit button)
                    }}
                    defaultTab="register"
                />
            )}

            {/* RESULTS VIEW */}
            {lastAuditResponse && (
                <div className="space-y-6">

                    {/* ── Freshness banner: report is stale if estate was edited
                        after purchase. */}
                    {fullAccess && lastAuditResponse.reportUpToDate === false && (
                        <ReportOutdatedBanner onUpdate={handleUpgrade} />
                    )}

                    {/* ─────────────────────────────────────────────────────────
                        Chambers report body — same layout as the print version.
                        Free users still get the BasicEstateSummary fallback.
                       ───────────────────────────────────────────────────────── */}
                    {fullAccess ? (
                        <>
                            <ValidationGuardBanner ihtCalc={ihtCalc} />
                            <TrustEstateInclusionNotice ihtCalc={ihtCalc} />

                            {complexityFlags?.estateAbove2M && (
                                <ScenarioInlineHint message={getInlineHint('above_2m')} />
                            )}

                            {ihtCalc && lastAuditResponse.actionPack && (
                                <div className="bg-[#FAFAF8] border border-[#E8E4DB] rounded-xl">
                                    <ReportBody
                                        profile={data.EstateProfile}
                                        assets={data.EstateAssets}
                                        ihtCalc={ihtCalc}
                                        actionPack={lastAuditResponse.actionPack}
                                        probateConfidence={lastAuditResponse.derived?.probate_confidence_level}
                                        consistencyFindings={consistencyFindings}
                                        goToStep={goToStep}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <BasicEstateSummary
                            grossEstateValue={lastAuditResponse.derived?.gross_estate_value}
                            netEstateValue={lastAuditResponse.derived?.net_estate_value}
                            thresholdStatus={lastAuditResponse.derived?.threshold_status}
                            estimatedIht={lastAuditResponse.derived?.estimated_iht}
                            headline={lastAuditResponse.summary}
                            upgradeMessage={upgradeMessage}
                            complexityHints={complexityHints}
                            trustEstateInclusionUnresolved={lastAuditResponse.derived?.trust_estate_inclusion_unresolved === true}
                            onUpgrade={handleUpgrade}
                        />
                    )}

                    {/* ══════════════════════════════════════════════════════════
                        GATED SECTIONS — only shown when fullAccess is true.
                        Free users see a single upgrade banner instead.
                       ══════════════════════════════════════════════════════════ */}
                    {fullAccess ? (
                        <>
                            {/* Outstanding items, risks, and planning opportunities now
                                live inside the Chambers ReportBody above. The panels
                                kept here add screen-only affordances (countdown,
                                evidence checklist, complexity score). */}

                            {/* ── Key Deadlines ────────────────────────────────────── */}
                            <DeadlineTracker
                                dateOfDeath={data.EstateProfile.deceased?.date_of_death}
                                routeKey={lastAuditResponse.actionPack?.recommended_pathway}
                                hasTaxDue={(ihtCalc?.total_tax_due ?? 0) > 0}
                                hasProperty={data.EstateAssets.properties.length > 0}
                                taxPositionUnresolved={!!ihtCalc?.breakdown?.trust_estate_inclusion_gap}
                            />

                            {/* ── Executor Tax Warning (urgent) ────────────────────── */}
                            <ExecutorTaxWarning
                                ihtCalc={ihtCalc}
                                dateOfDeath={data.EstateProfile.deceased?.date_of_death}
                            />

                            {/* ── Documents Still Required ──────────────────────────── */}
                            <EvidenceRequestPanel
                                assets={data.EstateAssets}
                                profile={data.EstateProfile}
                            />

                            {/* ── Probate Complexity ─────────────────────────────── */}
                            <ComplexityScorePanel
                                ihtCalc={ihtCalc}
                                assets={data.EstateAssets}
                                profile={data.EstateProfile}
                            />

                            {/* ── Reliability & Completeness ─────────────────────── */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <EstimateConfidence
                                    ihtCalc={ihtCalc}
                                    assets={data.EstateAssets}
                                    profile={data.EstateProfile}
                                />
                                <EstateCompleteness
                                    assets={data.EstateAssets}
                                    profile={data.EstateProfile}
                                    goToStep={goToStep}
                                />
                            </div>
                            <p className="text-[11px] text-[#AFA99F] italic text-center -mt-2">
                                The estimate can still be reliable even if some optional details are incomplete.
                            </p>
                        </>
                    ) : paymentPending ? (
                        <PaymentPendingCard onRefresh={handleRunAudit} refreshing={loading} />
                    ) : (
                        <LockedNextStepsPreview onUpgrade={handleUpgrade} upgradeMessage={upgradeMessage} thresholdStatus={lastAuditResponse?.derived?.threshold_status} estimatedIht={lastAuditResponse?.derived?.estimated_iht} trustEstateInclusionUnresolved={lastAuditResponse?.derived?.trust_estate_inclusion_unresolved === true} />
                    )}

                    {/* ════════════════════════════════════════════════════════════
                        COLLAPSIBLE DETAIL SECTIONS
                        Professional-level detail, hidden by default.
                        Only available to paid users.
                       ════════════════════════════════════════════════════════════ */}

                    {fullAccess && <div className="pt-2">
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-4 px-1">
                            Detailed analysis
                        </p>
                        <div className="space-y-3">

                            {/* ── 9. Calculation details ─────────────────────── */}
                            <CollapsibleSection
                                title="Show calculation details"
                                icon={<Calculator className="w-4 h-4 text-[#6B6762]" />}
                            >
                                <div className="p-6 space-y-6">
                                    <InputsUsedPanel profile={data.EstateProfile} goToStep={goToStep} />

                                    <IHTCalculatorPanel
                                        profile={data.EstateProfile}
                                        ihtCalc={ihtCalc}
                                        availableProperties={data.EstateAssets.properties}
                                        onChange={() => { }}
                                        mode="results"
                                    />

                                    <TaxSourceBreakdown
                                        ihtCalc={ihtCalc}
                                        assets={data.EstateAssets}
                                    />

                                    {complexityFlags?.hasRnrbTaper && <ScenarioInlineHint message={getInlineHint('rnrb_taper')} />}
                                    {complexityFlags?.hasNoRnrb && !complexityFlags?.hasRnrbTaper && <ScenarioInlineHint message={getInlineHint('no_rnrb')} />}
                                    <RnrbExplanation
                                        ihtCalc={ihtCalc}
                                        assets={data.EstateAssets}
                                        ihtDetails={data.EstateProfile.iht_details}
                                    />

                                    {data.EstateAssets.properties.length > 0 && (
                                        <PropertyRationalePanel properties={data.EstateAssets.properties} ihtDetails={data.EstateProfile.iht_details} />
                                    )}
                                </div>
                            </CollapsibleSection>

                            {/* ── 10. Lifetime gift analysis ─────────────────── */}
                            <CollapsibleSection
                                title="Show lifetime gift analysis"
                                icon={<Gift className="w-4 h-4 text-[#6B6762]" />}
                            >
                                <div className="p-6 space-y-3">
                                    {complexityFlags?.hasLifetimeGifts && <ScenarioInlineHint message={getInlineHint('gifts')} />}
                                    <GiftTaperBreakdown ihtCalc={ihtCalc} />
                                </div>
                            </CollapsibleSection>

                            {/* ── 11. HMRC forms required ────────────────────── */}
                            <CollapsibleSection
                                title="Show HMRC forms required"
                                icon={<FileText className="w-4 h-4 text-[#6B6762]" />}
                            >
                                <div className="p-6">
                                    <HMRCFormMapper
                                        assets={data.EstateAssets}
                                        profile={data.EstateProfile}
                                        routeKey={lastAuditResponse.actionPack?.recommended_pathway}
                                        ihtCalc={ihtCalc}
                                    />
                                </div>
                            </CollapsibleSection>

                            {/* ── 12. Full risk detail ──────────────────────── */}
                            <CollapsibleSection
                                title="Show full risk detail"
                                icon={<ShieldAlert className="w-4 h-4 text-[#6B6762]" />}
                            >
                                <div className="p-6 space-y-6">
                                    <IHTRiskFlagsPanel
                                        ihtCalc={ihtCalc}
                                        assets={data.EstateAssets}
                                        profile={data.EstateProfile}
                                    />
                                    <ProbateRiskOverview
                                        ihtCalc={ihtCalc}
                                        assets={data.EstateAssets}
                                        profile={data.EstateProfile}
                                    />
                                    <ExecutorRiskAlerts
                                        ihtCalc={ihtCalc}
                                        assets={data.EstateAssets}
                                        profile={data.EstateProfile}
                                    />
                                </div>
                            </CollapsibleSection>

                            {/* ── 14. Detailed checklist ─────────────────────── */}
                            <CollapsibleSection
                                title="Show detailed checklist"
                                icon={<ListChecks className="w-4 h-4 text-[#6B6762]" />}
                            >
                                <div className="p-6">
                                    <SubmissionChecklist
                                        ihtCalc={ihtCalc}
                                        assets={data.EstateAssets}
                                        profile={data.EstateProfile}
                                        routeKey={lastAuditResponse.actionPack?.recommended_pathway}
                                    />
                                </div>
                            </CollapsibleSection>
                        </div>
                    </div>}

                    {/* ── Download link ────────────────────────────────────────── */}
                    {fullAccess ? (
                        <div className="flex justify-center">
                            <button
                                onClick={handleDownloadReport}
                                disabled={downloadPreparing}
                                className="flex items-center gap-2 text-[13px] font-medium text-[#3730A3] hover:text-[#2E2780] hover:underline decoration-[#C7CBEA] underline-offset-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Download className="w-4 h-4" />
                                {downloadPreparing ? 'Preparing your report…' : 'Download probate report (PDF)'}
                            </button>
                        </div>
                    ) : (
                        <div className="bg-[#FAFAF8] border border-dashed border-[#E8E4DB] p-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-[#AFA99F]">
                                <Download className="w-4 h-4" />
                                <span className="text-[13px]">Download probate report (PDF)</span>
                            </div>
                            <button
                                onClick={handleUpgrade}
                                className="text-[12px] font-medium text-[#3730A3] hover:text-[#2E2780] hover:underline decoration-[#C7CBEA] underline-offset-4 transition-colors"
                            >
                                Unlock for {PRICE_LABEL}
                            </button>
                        </div>
                    )}

                    {/* ── Research Assistant ───────────────────────────────────── */}
                    <WizardResearchPanel
                        data={data}
                        ihtCalc={ihtCalc}
                        fullAccess={fullAccess}
                        estateId={estateId}
                        onUpgrade={handleUpgrade}
                        onShowAuth={() => setShowAuthModal(true)}
                    />

                    {/* ── Final upgrade CTA (free users only) ────────────────── */}
                    {!fullAccess && !paymentPending && <PricingPanel onUpgrade={handleUpgrade} upgradeMessage={upgradeMessage} trustEstateInclusionUnresolved={lastAuditResponse?.derived?.trust_estate_inclusion_unresolved === true} />}

                    {/* ── Advanced & Exports ───────────────────────────────────── */}
                    <div className="border border-[#E8E4DB] bg-white">
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="w-full flex items-center justify-between p-4 bg-[#FAFAF8] hover:bg-[#F3EFE6] transition-colors text-left"
                        >
                            <span className="text-[13px] font-medium text-[#1A1A1A] flex items-center gap-2">
                                <Database className="w-4 h-4 text-[#6B6762]" />
                                Advanced &amp; exports
                            </span>
                            {showAdvanced ? <ChevronUp className="w-4 h-4 text-[#6B6762]" /> : <ChevronDown className="w-4 h-4 text-[#6B6762]" />}
                        </button>

                        {showAdvanced && (
                            <div className="p-6 space-y-6 animate-in slide-in-from-top-2">
                                {/* Export Actions */}
                                <div>
                                    <h4 className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-3 m-0">Export data</h4>
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            onClick={() => fullAccess
                                                ? generateAndOpenProbateReport(data, lastAuditResponse)
                                                : handleUpgrade()}
                                            className="flex items-center gap-2 px-5 py-3 text-[13px] font-medium tracking-[0.02em] text-white bg-[#3730A3] hover:bg-[#2E2780] transition-colors"
                                        >
                                            <Download className="w-4 h-4" /> {fullAccess ? 'Download report (PDF)' : 'Get full report'}
                                        </button>
                                        <button
                                            className="flex items-center gap-2 px-5 py-3 text-[13px] font-medium text-[#3F3D39] bg-white border border-[#E8E4DB] hover:bg-[#F3EFE6] transition-colors opacity-50 cursor-not-allowed"
                                            title="JSON Export coming soon"
                                        >
                                            <FileJson className="w-4 h-4" /> Export JSON
                                        </button>
                                    </div>
                                    <p className="text-[11px] text-[#AFA99F] mt-2 italic">
                                        Opens a print-ready report. Select <strong>Save as PDF</strong> in your browser's print dialog.
                                        {!lastAuditResponse && <span className="text-[#7A5A00]"> Run the analysis first for the full calculation breakdown.</span>}
                                    </p>
                                </div>

                                {/* Reasoning Log (Formerly in ActionPack) */}
                                {whyItems.length > 0 && (
                                    <div>
                                        <h4 className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-3 m-0">Logic reasoning</h4>
                                        <div className="bg-[#FAFAF8] border border-[#E8E4DB] p-4 space-y-2">
                                            {whyItems.map((item, idx) => (
                                                <div key={idx} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 text-[12px] font-mono border-b border-dotted border-[#E8E4DB] pb-2 last:border-0 last:pb-0">
                                                    <span className="font-medium text-[#1A1A1A] min-w-[200px]">{item.decision_key}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-1.5 py-0.5 font-medium
                                                                ${item.outcome === 'true' ? 'bg-[#EEF0FA] text-[#3730A3]' :
                                                                item.outcome === 'false' ? 'bg-[#F3EFE6] text-[#3F3D39]' : 'bg-[#FAF3E0] text-[#7A5A00]'}`}>
                                                            {item.outcome}
                                                        </span>
                                                        <span className="text-[#6B6762]">({item.reason_code})</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Debug Tools */}
                                <div>
                                    <h4 className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-3 m-0">Debug tools</h4>
                                    <div className="bg-[#1A1A1A] p-4 text-[#AFA99F] text-[13px] flex items-center justify-between">
                                        <span>View raw audit response and system logs.</span>
                                        <button
                                            onClick={() => setShowDebug(true)}
                                            className="px-4 py-2 bg-[#3F3D39] hover:bg-[#1A1A1A] text-white border border-[#3F3D39] hover:border-white text-[12px] font-medium tracking-[0.02em] transition-colors"
                                        >
                                            Open debug panel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quick Edit (Bottom) */}
                    <div className="mt-8 pt-8 border-t border-[#E8E4DB]">
                        <div className="text-center mb-6">
                            <h3 className="font-serif text-[22px] font-normal tracking-[-0.01em] text-[#1A1A1A] m-0">Need to make changes?</h3>
                            <button
                                onClick={() => handleRunAudit()}
                                className="mt-2 text-[13px] font-medium text-[#3730A3] hover:text-[#2E2780] hover:underline decoration-[#C7CBEA] underline-offset-4 transition-colors"
                            >
                                Re-run audit after editing
                            </button>
                        </div>
                        <div>
                            <EstateBuilder data={data.EstateAssets} onChange={updateAssets} />
                        </div>
                    </div>
                </div>
            )}

            <DebugPanel
                isOpen={showDebug}
                onClose={() => setShowDebug(false)}
                result={lastAuditResponse}
                sessionNonce={sessionNonce}
                goToStep={goToStep}
                componentName="ReviewStep"
            />

            {/* Print Only Report View */}
            {lastAuditResponse && lastAuditResponse.derived?.iht_calc && lastAuditResponse.actionPack && (
                <div id="probate-report-view-wrapper">
                    <ErrorBoundary sectionName="Probate Report (Print View)">
                        <ProbateReport
                            profile={data.EstateProfile}
                            assets={data.EstateAssets}
                            ihtCalc={lastAuditResponse.derived.iht_calc}
                            actionPack={lastAuditResponse.actionPack}
                            probateConfidence={lastAuditResponse.derived.probate_confidence_level}
                        />
                    </ErrorBoundary>
                </div>
            )}
        </div>
    );
};
